import React, {useEffect, useState} from "react";
import FetchAttachmentBlobFunction from "./FetchAttachmentBlob.function";
import {AttachmentRendererProps, RendererTypes} from "types/attachment";
import Image from "./attachmentTypes/Images/Image.function";
import Pdf from "./attachmentTypes/Pdf.function";
import Html from "./attachmentTypes/Html.function";
import PDFAlternativeView from "./attachmentTypes/PdfAlternativeView.function";
import Unknown from "./attachmentTypes/Unknown.function";
import {IMAGE_MIME_TYPE} from "utils/constants";
import {BypassCIRSkeletonContent} from "./BypassCIR.Skeleton";
import Immutable from "seamless-immutable";
import AttachmentInformation from "components/taskdetails-remake/panels/attachments/AttachmentInformation";
import {useSelector} from "react-redux";
import {Store} from "redux";
import * as reduxSelectors from "components/store/application.reducers";
import {snowplowTrackEvent} from "utils/snowplow";
import Unavailable from "components/taskdetails-remake/panels/attachments/attachmentTypes/Unavailable.function";


const MimeTypeRenderer: RendererTypes = Immutable.from({
    "application/pdf": Pdf,
    "application/html": Html,
    "text/html": Html,
    "image/png": Image,
    "image/jpeg": Image,
    "image/jpg": Image,
    "image/tiff": Image,
    "text/xml": Unknown,

    asComponent(key: string) {
        return this.hasOwnProperty(key) ? this[key] : Unknown;
    }
});


const AttachmentRenderer = ({
                                attachment,
                                isPanelCollapsed,
                                collapseCallback,
                                ownerDocument,
                                uid,
                                docVersion
                            }: AttachmentRendererProps): React.ReactElement => {
    const [url, setUrl] = useState<string>("");
    const [fetchError, setFetchError] = useState<boolean>(false);
    const userData = useSelector((store: Store) => reduxSelectors.getUsersData(store));
    const showDisplayAlternativePdfViewer = !navigator?.pdfViewerEnabled || (window.localStorage.getItem("@@pdf/defaultPDF") && window.localStorage.getItem("@@pdf/defaultPDF") !== "true");

    const getAttachmentType = () => {
        if (attachment.mimeType === null || !attachment.mimeType)
            return Unknown;

        if (attachment.mimeType.includes("pdf") && showDisplayAlternativePdfViewer) {
            return PDFAlternativeView;
        }

        return MimeTypeRenderer.asComponent(attachment.mimeType) || BypassCIRSkeletonContent;
    }

    const loadAttachments = () => {
        if (!Array.isArray(attachment.attachmentIds) || attachment.attachmentIds.length === 0) {
            console.error("Attachment IDs are missing or invalid.");
            return;
        }
        FetchAttachmentBlobFunction(attachment.originalUrl, attachment.mimeType, userData, uid, docVersion, attachment.attachmentIds[0]).then((result: string) => {
            setUrl(result)
        }).catch(() => {
            setFetchError(true);
        });
    }
    useEffect(() => {
        if (attachment.mimeType === null || !attachment.mimeType)
            return;
        if (!attachment.mimeType.includes(IMAGE_MIME_TYPE) && url === "") {
            loadAttachments();
        }

        if (showDisplayAlternativePdfViewer) {
            snowplowTrackEvent({
                category: "alternative-pdf-option",
                action: "select-alternative-pdf-option",
                label: "alternative-pdf-option",
                value: showDisplayAlternativePdfViewer.toString()
            }, userData);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const AttachmentRenderer = getAttachmentType();
    const hasAttachmentInformation = attachment.information?.uploaderId !== 0;
    const collapsedClass = isPanelCollapsed ? 'info-collapsed' : 'info-expanded';
    const attachmentRendererWrapperClass = hasAttachmentInformation ? `attachment-container-with-info ${collapsedClass}` : "attachment-container-no-info";

    return (
        <>
            {hasAttachmentInformation &&
                <div className={`p-16 bg-white border-radius-bottom-4`}>
                    <AttachmentInformation {...attachment.information}
                                           canBeDeleted={attachment.deleteUrlTemplate}
                                           isPanelCollapsed={isPanelCollapsed}
                                           collapseCallback={collapseCallback}/>
                </div>}
            <div
                className={attachmentRendererWrapperClass}>
                {fetchError ?
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <Unavailable/>
                    </div>
                    :
                    <AttachmentRenderer collection={attachment} url={url}
                                        callback={loadAttachments}
                                        uid={uid} docVersion={docVersion}
                                        ownerDocument={ownerDocument}/>
                }
            </div>
        </>
    )
}
export default AttachmentRenderer;




